import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';
import {
  Users, Wallet, Clock, TrendingUp,
  Building2, MapPin, Briefcase, ArrowUpRight,
  Loader2, RefreshCw, Download, FileSpreadsheet,
  FileText, Printer, AlertCircle, Award, Calendar,
  Search, Filter, ShieldAlert, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  LineChartComponent,
  BarChartComponent,
  AreaChartComponent,
  PieChartComponent,
  ComposedChartComponent,
  SummaryCardComponent,
} from '../components/charts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Formatting utilities
const fmt = (n) => {
  if (n === undefined || n === null) return '0';
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return Number(n).toLocaleString('en-IN');
};

const inr = (n) => `INR ${fmt(n)}`;

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('summary'); // 'summary' | 'workforce' | 'payroll' | 'attendance' | 'finance' | 'reports'
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Global filters state
  const [department, setDepartment] = useState('all');
  const [location, setLocation] = useState('all');
  const [employmentType, setEmploymentType] = useState('all');

  // Dynamic filter options loaded from backend
  const [filterOptions, setFilterOptions] = useState({
    departments: [],
    locations: [],
    employment_types: [],
  });

  // Backend state datasets
  const [dashboardData, setDashboardData] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [financeData, setFinanceData] = useState(null);

  // Tab 6: Detailed Reports State
  const [reportsData, setReportsData] = useState([]);
  const [reportsPagination, setReportsPagination] = useState({ page: 1, limit: 25, total: 0, total_pages: 1 });
  const [reportSearch, setReportSearch] = useState('');
  const [reportPage, setReportPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1. Fetch companies
  useEffect(() => {
    axios
      .get(`${API_URL}/companies`, { withCredentials: true })
      .then((res) => {
        const list = res.data || [];
        setCompanies(list);
        if (list.length && !selectedCompany) {
          setSelectedCompany(list[0].id);
        }
      })
      .catch(() => {});
  }, [selectedCompany, setSelectedCompany]);

  // 2. Fetch dynamic filter options whenever selectedCompany changes
  useEffect(() => {
    if (!selectedCompany) return;
    axios
      .get(`${API_URL}/analytics/filter-options`, {
        params: { company_id: selectedCompany },
        withCredentials: true,
      })
      .then((res) => {
        setFilterOptions(res.data || { departments: [], locations: [], employment_types: [] });
      })
      .catch((err) => console.error('Failed to load filter options:', err));
  }, [selectedCompany]);

  // 3. Fetch Tab & Analytics Datasets with FULL Global Filter Propagation
  const fetchTabData = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    setError(null);
    try {
      const compId = selectedCompany || undefined;
      const commonParams = {
        company_id: compId,
        period,
        department: department !== 'all' ? department : undefined,
        location: location !== 'all' ? location : undefined,
        employment_type: employmentType !== 'all' ? employmentType : undefined,
      };

      const [dashRes, payRes, attRes, empRes, finRes, rptRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/dashboard`, { params: commonParams, withCredentials: true }),
        axios.get(`${API_URL}/analytics/payroll`, { params: commonParams, withCredentials: true }),
        axios.get(`${API_URL}/analytics/attendance`, { params: { ...commonParams, month: period }, withCredentials: true }),
        axios.get(`${API_URL}/analytics/employees`, { params: commonParams, withCredentials: true }),
        axios.get(`${API_URL}/analytics/finance`, { params: commonParams, withCredentials: true }),
        axios.get(`${API_URL}/analytics/reports`, {
          params: { ...commonParams, search: reportSearch, page: reportPage, limit: 25 },
          withCredentials: true,
        }),
      ]);

      setDashboardData(dashRes.data);
      setPayrollData(payRes.data);
      setAttendanceData(attRes.data);
      setEmployeeData(empRes.data);
      setFinanceData(finRes.data);
      setReportsData(rptRes.data?.data || []);
      setReportsPagination(rptRes.data?.pagination || { page: 1, limit: 25, total: 0, total_pages: 1 });
    } catch (err) {
      console.error('Analytics API error:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, period, department, location, employmentType, reportSearch, reportPage]);

  useEffect(() => {
    fetchTabData();
  }, [fetchTabData]);

  const handleResetFilters = () => {
    setDepartment('all');
    setLocation('all');
    setEmploymentType('all');
    setReportSearch('');
    setReportPage(1);
    toast.success('Global filters reset to default');
  };

  const handleExportCSV = async () => {
    try {
      const compId = selectedCompany || undefined;
      const params = new URLSearchParams({
        company_id: compId || '',
        period,
        format: 'csv',
      });
      if (department !== 'all') params.set('department', department);
      if (location !== 'all') params.set('location', location);
      if (employmentType !== 'all') params.set('employment_type', employmentType);
      if (reportSearch) params.set('search', reportSearch);

      const res = await axios.get(`${API_URL}/analytics/reports?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });

      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_detailed_report_${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Detailed report exported to CSV successfully');
    } catch (err) {
      toast.error('CSV Export failed: ' + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 pb-16" data-testid="analytics-dashboard-page">
        {/* Header Title bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Executive BI Analytics</h1>
            <p className="mt-1 text-sm text-slate-500">Real-time enterprise intelligence & workforce analytics</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchTabData}
              className="p-2 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg text-slate-600 transition-colors"
              title="Refresh Analytics"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 shadow-xs"
            >
              <Download className="w-3.5 h-3.5 text-blue-600" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Sticky Global Filters Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs space-y-3 sticky top-4 z-10" data-testid="analytics-controls">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-blue-600" /> Global Analytics Filters
            </span>
            <button onClick={handleResetFilters} className="text-xs font-semibold text-blue-600 hover:underline">
              Reset Filters
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-xs">
            {user?.role === 'super_admin' && (
              <div>
                <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Company</label>
                <select
                  data-testid="analytics-company-select"
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full border border-slate-200 rounded px-2.5 py-1.5 bg-slate-50 font-medium text-slate-700"
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-full border border-slate-200 rounded px-2.5 py-1.5 bg-slate-50 font-medium text-slate-700"
              >
                <option value="all">All Departments</option>
                {filterOptions.departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Location</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full border border-slate-200 rounded px-2.5 py-1.5 bg-slate-50 font-medium text-slate-700"
              >
                <option value="all">All Locations</option>
                {filterOptions.locations.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Employment Type</label>
              <select
                value={employmentType}
                onChange={(e) => setEmploymentType(e.target.value)}
                className="w-full border border-slate-200 rounded px-2.5 py-1.5 bg-slate-50 font-medium text-slate-700"
              >
                <option value="all">All Types</option>
                {filterOptions.employment_types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Period / Month</label>
              <input
                data-testid="analytics-period-input"
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full border border-slate-200 rounded px-2.5 py-1 bg-slate-50 text-slate-700 font-mono text-xs"
              />
            </div>
          </div>
        </div>

        {/* Tab Navigation Menu */}
        <div className="flex gap-1 bg-slate-100 border border-slate-200 rounded-xl p-1 overflow-x-auto" data-testid="analytics-tabs">
          {[
            { id: 'summary', label: 'Executive BI Summary', icon: Award },
            { id: 'workforce', label: 'Workforce', icon: Users },
            { id: 'payroll', label: 'Payroll', icon: Wallet },
            { id: 'attendance', label: 'Attendance & Leave', icon: Clock },
            { id: 'finance', label: 'CFO Insights', icon: TrendingUp },
            { id: 'reports', label: 'Detailed Reports', icon: Search },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`analytics-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                  tab === t.id
                    ? 'bg-white text-blue-600 shadow-xs border-b-2 border-b-blue-600'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
            <p className="text-xs text-slate-400 font-semibold tracking-wider uppercase">Calculating BI Aggregations...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white border border-slate-200 rounded-xl text-center space-y-2">
            <AlertCircle className="w-10 h-10 text-amber-500" />
            <p className="text-sm font-semibold text-slate-700">{error}</p>
            <button onClick={fetchTabData} className="text-xs text-blue-600 hover:underline font-semibold mt-2">
              Retry Loading Data
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* 1. EXECUTIVE BI SUMMARY TAB */}
            {tab === 'summary' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <SummaryCardComponent
                    label="Total Headcount"
                    value={dashboardData?.headcount?.total || 0}
                    subtext={`Active: ${dashboardData?.headcount?.active || 0} · Joiners: ${dashboardData?.headcount?.new_joiners || 0}`}
                    change={`${dashboardData?.headcount?.attrition_rate || 0}% Attrition`}
                    changeType="positive"
                    icon={Users}
                    color="blue"
                  />
                  <SummaryCardComponent
                    label="Net Disbursement"
                    value={dashboardData?.payroll?.has_payroll_run ? inr(dashboardData?.payroll?.total_net || 0) : 'Payroll Not Processed'}
                    subtext={dashboardData?.payroll?.has_payroll_run ? `Gross: ${inr(dashboardData?.payroll?.total_gross || 0)}` : `Period: ${period}`}
                    icon={Wallet}
                    color="green"
                  />
                  <SummaryCardComponent
                    label="Average Attendance"
                    value={dashboardData?.attendance?.has_attendance_records ? `${dashboardData?.attendance?.present_percentage}%` : 'No Records'}
                    subtext={dashboardData?.attendance?.has_attendance_records ? `Late: ${dashboardData?.attendance?.late_arrivals} · OT: ${dashboardData?.attendance?.total_ot_hours} hrs` : `Period: ${period}`}
                    icon={Clock}
                    color="purple"
                  />
                  <SummaryCardComponent
                    label="Company CTC Liability"
                    value={inr(dashboardData?.payroll?.annual_ctc_liability || 0)}
                    subtext={`Annualized CTC Exposure`}
                    icon={TrendingUp}
                    color="indigo"
                  />
                </div>

                {/* AI Smart Insights Panel */}
                <div className="bg-gradient-to-r from-blue-50/60 to-indigo-50/60 border border-blue-100 rounded-2xl p-5 shadow-xs space-y-3">
                  <div className="flex items-center justify-between border-b border-blue-200/50 pb-2">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                      <AlertCircle className="w-4 h-4 text-blue-600" /> AI Smart Insights
                    </h3>
                    <span className="px-2 py-0.5 bg-blue-600 text-white rounded-full text-[9px] font-bold uppercase tracking-wider">
                      Live Analysis
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-700">
                    {(dashboardData?.ai_insights || []).map((insight, idx) => (
                      <p key={idx} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 shrink-0" />
                        <span>{insight}</span>
                      </p>
                    ))}
                  </div>
                </div>

                {/* Recharts Distributions */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <PieChartComponent
                    title="Department Headcount Share"
                    data={dashboardData?.departments || []}
                    dataKey="value"
                    nameKey="name"
                    donut={true}
                    height={260}
                    loading={loading}
                  />

                  <PieChartComponent
                    title="Gender Diversity Ratio"
                    data={dashboardData?.genders || []}
                    dataKey="value"
                    nameKey="name"
                    donut={true}
                    colors={['#3B82F6', '#EC4899', '#F59E0B']}
                    height={260}
                    loading={loading}
                  />

                  <BarChartComponent
                    title="Top Locations Distribution"
                    data={dashboardData?.locations || []}
                    xAxisKey="name"
                    bars={[{ key: 'value', name: 'Employees', color: '#10B981' }]}
                    height={260}
                    loading={loading}
                  />
                </div>

                {/* MoM Gross Salary vs Headcount */}
                <ComposedChartComponent
                  title="MoM Gross Salary vs Headcount (Past Runs)"
                  data={dashboardData?.mom_trend || []}
                  xAxisKey="period"
                  series={[
                    { type: 'bar', key: 'gross', name: 'Gross Pay (INR)', color: '#3B82F6' },
                    { type: 'line', key: 'headcount', name: 'Headcount', color: '#F59E0B', yAxisId: 'right' },
                  ]}
                  formatterLeft={(v) => fmt(v)}
                  height={260}
                  loading={loading}
                />
              </div>
            )}

            {/* 2. WORKFORCE TAB */}
            {tab === 'workforce' && employeeData && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <SummaryCardComponent label="Total Headcount" value={employeeData.summary.total} icon={Users} color="blue" />
                  <SummaryCardComponent label="Active" value={employeeData.summary.active} icon={Users} color="green" />
                  <SummaryCardComponent label="Attrition Rate" value={`${employeeData.summary.attrition_rate}%`} icon={TrendingUp} color="red" />
                  <SummaryCardComponent label="New Joiners" value={employeeData.summary.new_joiners} icon={ArrowUpRight} color="indigo" />
                  <SummaryCardComponent label="Separated" value={employeeData.summary.separated} icon={Briefcase} color="purple" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <BarChartComponent
                    title="Department Distribution"
                    data={employeeData.department_distribution || []}
                    xAxisKey="name"
                    bars={[{ key: 'value', name: 'Employees', color: '#3B82F6' }]}
                    height={300}
                    loading={loading}
                  />

                  <BarChartComponent
                    title="Location Distribution"
                    data={employeeData.location_distribution || []}
                    xAxisKey="name"
                    bars={[{ key: 'value', name: 'Employees', color: '#10B981' }]}
                    height={300}
                    loading={loading}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <PieChartComponent
                    title="Employment Type Share"
                    data={employeeData.employment_type_distribution || []}
                    dataKey="value"
                    nameKey="name"
                    donut={true}
                    height={280}
                    loading={loading}
                  />

                  <PieChartComponent
                    title="Gender Diversity Ratio"
                    data={employeeData.gender_distribution || []}
                    dataKey="value"
                    nameKey="name"
                    donut={true}
                    colors={['#3B82F6', '#EC4899', '#F59E0B']}
                    height={280}
                    loading={loading}
                  />
                </div>
              </div>
            )}

            {/* 3. PAYROLL TAB */}
            {tab === 'payroll' && payrollData && (
              <div className="space-y-6 animate-in fade-in duration-300">
                {!payrollData.has_payroll_run && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-xs font-semibold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
                    <span>No payroll run processed for period {payrollData.period} matching the active global filters. Displaying annualized CTC salary slabs.</span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <SummaryCardComponent label="Total Gross Cost" value={payrollData.has_payroll_run ? inr(payrollData.summary.total_gross) : 'Payroll Not Run'} icon={Wallet} color="blue" />
                  <SummaryCardComponent label="Net Disbursed" value={payrollData.has_payroll_run ? inr(payrollData.summary.total_net) : 'Payroll Not Run'} icon={Wallet} color="green" />
                  <SummaryCardComponent label="Total Deductions" value={payrollData.has_payroll_run ? inr(payrollData.summary.total_deductions) : 'Payroll Not Run'} icon={Briefcase} color="purple" />
                  <SummaryCardComponent label="Avg Compensation" value={payrollData.has_payroll_run ? inr(payrollData.summary.avg_salary) : '—'} sub={`${payrollData.summary.headcount} employees`} icon={Users} color="indigo" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <BarChartComponent
                    title="Department-wise Gross Salary Cost"
                    data={payrollData.department_breakdown || []}
                    xAxisKey="name"
                    bars={[
                      { key: 'gross', name: 'Gross Pay (INR)', color: '#3B82F6' },
                      { key: 'net', name: 'Net Pay (INR)', color: '#10B981' },
                    ]}
                    formatter={(v) => fmt(v)}
                    height={320}
                    loading={loading}
                  />

                  <BarChartComponent
                    title="Salary Slab Distribution"
                    data={payrollData.salary_slabs || []}
                    xAxisKey="name"
                    bars={[{ key: 'value', name: 'Employee Count', color: '#6366F1' }]}
                    height={320}
                    loading={loading}
                  />
                </div>

                <AreaChartComponent
                  title="Historical MoM Payroll Trend"
                  data={payrollData.historical_trend || []}
                  xAxisKey="period"
                  areas={[
                    { key: 'gross', name: 'Gross Cost (INR)', color: '#3B82F6' },
                    { key: 'net', name: 'Net Disbursed (INR)', color: '#10B981' },
                    { key: 'deductions', name: 'Deductions (INR)', color: '#EF4444' },
                  ]}
                  formatter={(v) => fmt(v)}
                  height={300}
                  loading={loading}
                />
              </div>
            )}

            {/* 4. ATTENDANCE & LEAVE TAB */}
            {tab === 'attendance' && attendanceData && (
              <div className="space-y-6 animate-in fade-in duration-300">
                {!attendanceData.has_attendance_records && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-xs font-semibold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
                    <span>No daily attendance records logged for month {attendanceData.month} matching active filters.</span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <SummaryCardComponent label="Present %" value={attendanceData.has_attendance_records ? `${attendanceData.summary.present_percentage}%` : 'No Records'} sub={`${attendanceData.summary.total_records} records`} icon={Users} color="green" />
                  <SummaryCardComponent label="Late Arrivals Count" value={attendanceData.summary.late_arrivals} icon={Clock} color="amber" />
                  <SummaryCardComponent label="OT Hours" value={attendanceData.summary.total_ot_hours} sub={`${attendanceData.summary.ot_employee_count} employees`} icon={Clock} color="purple" />
                  <SummaryCardComponent label="Total Leaves Applied" value={attendanceData.summary.total_leaves_applied} icon={Calendar} color="blue" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <PieChartComponent
                    title="Daily Attendance Status Share"
                    data={attendanceData.status_breakdown || []}
                    dataKey="value"
                    nameKey="name"
                    donut={true}
                    colors={['#10B981', '#EF4444', '#F59E0B', '#6366F1', '#94A3B8']}
                    height={300}
                    loading={loading}
                  />

                  <AreaChartComponent
                    title="Daily Attendance Trend"
                    data={attendanceData.daily_trend || []}
                    xAxisKey="date"
                    areas={[
                      { key: 'present', name: 'Present', color: '#10B981' },
                      { key: 'absent', name: 'Absent', color: '#EF4444' },
                      { key: 'leave', name: 'Leave', color: '#F59E0B' },
                      { key: 'late', name: 'Late Arrivals', color: '#6366F1' },
                    ]}
                    height={300}
                    loading={loading}
                  />
                </div>
              </div>
            )}

            {/* 5. CFO & FINANCE TAB */}
            {tab === 'finance' && financeData && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <SummaryCardComponent label="Monthly CTC Liability" value={inr(financeData.summary.monthly_ctc_liability)} icon={Wallet} color="blue" />
                  <SummaryCardComponent label="Employer Contribution" value={inr(financeData.summary.employer_pf + financeData.summary.employer_esi)} icon={Briefcase} color="purple" />
                  <SummaryCardComponent label="Total Deductions Book" value={inr(financeData.summary.total_deductions)} icon={Wallet} color="red" />
                  <SummaryCardComponent label="Active Loan Balance" value={inr(financeData.summary.loan_book)} sub={`${financeData.summary.active_loans_count} active loans`} icon={TrendingUp} color="green" />
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs space-y-4">
                  <h3 className="font-bold text-xs uppercase tracking-wider text-slate-500">Financial Exposure Breakdown</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="cfo-exposure-summary">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Pending Reimbursements</p>
                      <p className="text-xl font-bold font-mono text-slate-900 mt-2">{inr(financeData.summary.reimbursement_exposure)}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{financeData.summary.pending_reimbursements_count} claims pending approval</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Active Advance Book</p>
                      <p className="text-xl font-bold font-mono text-slate-900 mt-2">{inr(financeData.summary.loan_book)}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{financeData.summary.active_loans_count} loans/advances open</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Full & Final Settlements</p>
                      <p className="text-xl font-bold font-mono text-amber-600 mt-2">{financeData.summary.pending_fnf_count}</p>
                      <p className="text-[10px] text-slate-400 mt-1">Pending final payroll calculation</p>
                    </div>
                  </div>
                </div>

                <AreaChartComponent
                  title="6-Month Financial Cost Trend"
                  data={financeData.cost_trend || []}
                  xAxisKey="period"
                  areas={[
                    { key: 'ctc', name: 'Estimated CTC Liability', color: '#6366F1' },
                    { key: 'gross', name: 'Gross Salary Cost', color: '#3B82F6' },
                    { key: 'net', name: 'Net Disbursed Amount', color: '#10B981' },
                  ]}
                  formatter={(v) => fmt(v)}
                  height={320}
                  loading={loading}
                />
              </div>
            )}

            {/* 6. DETAILED REPORTS TAB */}
            {tab === 'reports' && (
              <div className="space-y-4 animate-in fade-in duration-300" data-testid="analytics-reports-tab">
                {/* Search & Export Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-slate-200 p-4 rounded-xl shadow-xs">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-bold rounded-lg border border-blue-100">
                      Period: {period}
                    </span>
                    <span className="text-xs font-semibold text-slate-500">
                      Showing {reportsPagination.total} employee records
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="relative w-full sm:w-64">
                      <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search Employee, ID, Dept..."
                        value={reportSearch}
                        onChange={(e) => {
                          setReportSearch(e.target.value);
                          setReportPage(1);
                        }}
                        className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-1.5 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                    <button
                      onClick={handleExportCSV}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors shrink-0"
                    >
                      <Download className="w-3.5 h-3.5" /> Export CSV
                    </button>
                  </div>
                </div>

                {/* Detailed Table */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                  {reportsData.length === 0 ? (
                    <div className="text-center py-16 px-4">
                      <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm font-semibold text-slate-700">No matching employee records</p>
                      <p className="text-xs text-slate-400 mt-1">Try adjusting your global filters or search input.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left p-3 font-semibold text-slate-700">Employee</th>
                            <th className="text-left p-3 font-semibold text-slate-700">ID</th>
                            <th className="text-left p-3 font-semibold text-slate-700">Department</th>
                            <th className="text-left p-3 font-semibold text-slate-700">Location</th>
                            <th className="text-left p-3 font-semibold text-slate-700">Type</th>
                            <th className="text-right p-3 font-semibold text-slate-700">Gross Pay (Monthly)</th>
                            <th className="text-right p-3 font-semibold text-slate-700">Net Pay (Monthly)</th>
                            <th className="text-right p-3 font-semibold text-slate-700">Annual CTC</th>
                            <th className="text-center p-3 font-semibold text-slate-700">Payroll Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {reportsData.map((row) => (
                            <tr key={row.employee_id} className="hover:bg-slate-50/80 transition-colors">
                              <td className="p-3 font-semibold text-slate-900">{row.employee_name}</td>
                              <td className="p-3 font-mono font-medium text-slate-600">{row.employee_code}</td>
                              <td className="p-3 text-slate-700">{row.department}</td>
                              <td className="p-3 text-slate-700">{row.location}</td>
                              <td className="p-3 text-slate-700">{row.employment_type}</td>
                              <td className="p-3 text-right">
                                {row.gross_pay !== null ? (
                                  <span className="font-semibold text-slate-900">{inr(row.gross_pay)}</span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">
                                    Payroll Not Processed
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                {row.net_pay !== null ? (
                                  <span className="font-semibold text-emerald-700">{inr(row.net_pay)}</span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">
                                    Payroll Not Processed
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-right font-bold text-slate-900">{inr(row.annual_ctc)}</td>
                              <td className="p-3 text-center">
                                <span
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                    row.payroll_status === 'Processed'
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'bg-amber-100 text-amber-800'
                                  }`}
                                >
                                  {row.payroll_status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination Footer */}
                  {reportsPagination.total_pages > 1 && (
                    <div className="flex items-center justify-between p-3 border-t border-slate-200 bg-slate-50/50 text-xs">
                      <span className="text-slate-500">
                        Page {reportsPagination.page} of {reportsPagination.total_pages} ({reportsPagination.total} total items)
                      </span>
                      <div className="flex gap-2">
                        <button
                          disabled={reportPage <= 1}
                          onClick={() => setReportPage((p) => Math.max(1, p - 1))}
                          className="px-3 py-1 border border-slate-200 rounded bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 font-medium"
                        >
                          <ChevronLeft className="w-3.5 h-3.5 inline mr-1" /> Previous
                        </button>
                        <button
                          disabled={reportPage >= reportsPagination.total_pages}
                          onClick={() => setReportPage((p) => Math.min(reportsPagination.total_pages, p + 1))}
                          className="px-3 py-1 border border-slate-200 rounded bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 font-medium"
                        >
                          Next <ChevronRight className="w-3.5 h-3.5 inline ml-1" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AnalyticsDashboard;
